import { action, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { Prompt } from 'react-router-dom';

import { CustomerFitSampleLead } from '../../../models/SampleLead';
import Store from '../../../store';
import { onSignalsRefresh } from '../../components/model/signalsRefreshBus';
import Error from '../../components/error/Error';
import ScrollButton from '../../components/generic_components/ScrollButton';
import LeadSampleTable, {
  COLUMN_VISIBILITY_BAG,
  DEFAULT_COLUMN_VISIBILITY,
} from '../../components/model/LeadSampleTable';
import SignalsGroups from '../../components/rules/signals/SignalsGroups';
import Spinner from '../../components/Spinner';
import './reviewSignals.css';

interface ReviewSignalsPageProps {
  store?: Store;
}

interface ReviewSignalsPageState {
  isLoading: boolean;
  falseNegativesOnly: boolean;
  columnVisibility: Partial<Record<keyof CustomerFitSampleLead, boolean>>;
  /**
   * The definition a clicked signal in the sample points at. The nonce is what
   * makes clicking the same line twice work: the editor reopens on a change to
   * this object, and without it a second click would be indistinguishable from
   * the first.
   */
  focus: { group: number; rule: number; nonce: number } | null;
  /** A definition is open, so a reload would pull it out from under someone. */
  editing: boolean;
  /**
   * The assistant wrote the signals while a definition was open.
   *
   * Held rather than applied. Reloading replaces the whole document, and the
   * row being edited with it, so the reader is told and decides.
   */
  agentChanged: boolean;
}

/**
 * Signals and the lead sample on one page.
 *
 * Configuring a signal and checking what it renders is one task, and the two
 * halves of it were a page apart in different sections. The signals page said so
 * itself: it carried a link reading "Check out the result in the Sample page",
 * which is a product telling you its own navigation is wrong. That link is gone
 * because the sample is now underneath.
 *
 * Both halves also read the same validation data, so the merge removes a
 * duplicate fetch rather than adding one.
 *
 * Side by side, which the design asked for and which only became possible once
 * the definitions collapsed to one line each. Stacked, checking what an edit
 * rendered meant scrolling past the whole editor and back.
 *
 * The sample column sticks. That is the part that makes iterating quick: the
 * editor is as long as the model has definitions, and a sample that scrolled
 * away with it would put the answer off screen exactly when the question was
 * being asked.
 */
const ReviewSignalsPage = inject('store')(
  observer(
    class ReviewSignalsPage extends React.Component<
      ReviewSignalsPageProps,
      ReviewSignalsPageState
    > {
      constructor(props: ReviewSignalsPageProps) {
        super(props);

        makeObservable(this, { saveSignals: action });
        this.saveSignals = this.saveSignals.bind(this);
        this.loadSample = this.loadSample.bind(this);
        this.toggleFalseNegatives = this.toggleFalseNegatives.bind(this);
        this.jumpToSignal = this.jumpToSignal.bind(this);

        this.state = {
          isLoading: true,
          falseNegativesOnly: false,
          columnVisibility: DEFAULT_COLUMN_VISIBILITY,
          focus: null,
          editing: false,
          agentChanged: false,
        };
        this.reloadSignals = this.reloadSignals.bind(this);
      }

      /** Unsubscribes from the assistant's signal writes. */
      offSignalsRefresh: () => void;

      async componentDidMount() {
        const { store } = this.props;

        const saved = localStorage.getItem(COLUMN_VISIBILITY_BAG);
        if (saved) this.setState({ columnVisibility: JSON.parse(saved) });

        // One validation load for both halves. Two pages meant two.
        await store.loadValidationData({ isProcessing: true });
        await Promise.all([
          store.signalsPage.savingSignals
            ? Promise.resolve()
            : store.loadSignals(),
          this.loadSample(),
        ]);
        this.setState({ isLoading: false });

        // The assistant writes signals through the same routes the editor
        // uses, so the model changes and the browser never hears about it.
        // Same mechanism the tree pane uses for `tree_refresh`.
        this.offSignalsRefresh = onSignalsRefresh(() => {
          if (this.state.editing) {
            this.setState({ agentChanged: true });
            return;
          }
          void this.reloadSignals();
        });
      }

      componentWillUnmount() {
        this.offSignalsRefresh?.();
      }

      /**
       * Take the assistant's version.
       *
       * The sample goes with it: its rendered signals came from the
       * definitions that just changed, so leaving it would show the old
       * wording beside the new rules.
       */
      async reloadSignals() {
        const { store } = this.props;
        this.setState({ agentChanged: false });
        await store.loadSignals();
        void this.loadSample({ keepSample: true });
      }

      /**
       * Write the signals, and let the sample catch up on its own.
       *
       * Awaited, the re-score was part of what Save meant: the button stayed
       * busy and the definition stayed open through a query the person had not
       * asked for, which is most of why saving read as the page reloading.
       * Writing the configuration is the thing that was asked for and it is
       * the only thing this waits on.
       */
      async saveSignals() {
        const { store } = this.props;
        store.analyticsTrackEvent('Updated signals');
        await store.saveSignals();
        if (store.signalsPage.signalsError) return;
        // The SAME leads, re-rendered. The sample draws with TABLESAMPLE and no
        // repeatable seed, so refetching normally hands back a different
        // hundred and the lead whose signal you were checking is gone by the
        // time the answer arrives. Keeping them is what makes an edit legible.
        void this.loadSample({ keepSample: true });
      }

      /**
       * From a rendered signal in the sample to the definition that produced it.
       *
       * The whole argument for merging these two panels: the answer to "why does
       * this lead say that" used to be a different page in a different section,
       * found by reading the definitions until one matched.
       */
      jumpToSignal(group: number, rule: number) {
        this.setState((state) => ({
          focus: {
            group,
            rule,
            nonce: (state.focus?.nonce ?? 0) + 1,
          },
        }));
      }

      /**
       * `keepSample` re-renders the leads on screen; without it this draws a new
       * hundred, which is what the Refresh control and the cohort toggle mean.
       */
      async loadSample(options: { keepSample?: boolean } = {}) {
        await this.props.store.getSpotCheckLeads(
          this.state.falseNegativesOnly,
          false,
          options.keepSample ?? false
        );
      }

      toggleFalseNegatives() {
        this.props.store.analyticsTrackEvent(
          'Requested a new dataset to spot check'
        );
        this.setState(
          { falseNegativesOnly: !this.state.falseNegativesOnly },
          this.loadSample
        );
      }

      render() {
        const { store } = this.props;
        const { signalsPage, spotCheckPage, validationPage } = store;
        const { signalsError, signalsSaved, loadingSignals, signals } =
          signalsPage;
        const { loadSpotCheckError } = spotCheckPage;
        const {
          validation: { error: validationError },
        } = validationPage;

        if (this.state.isLoading) return <Spinner />;

        return (
          <div className="rsig mt-3 mb-5">
            <Prompt
              when={signalsPage.savingSignals}
              message="Signals are still saving. If you leave now, your changes may appear lost. Are you sure?"
            />

            {validationError && <Error message={validationError} />}
            {signalsError && (
              <Error message={signalsError.message} type={signalsError.type} />
            )}

            <div className="rsig-split">
              <Card className="rsig-card">
                <Card.Body>
                  <h5 className="rsig-title">
                    <i className="fas fa-bolt rsig-icon" aria-hidden="true" />
                    Signals
                  </h5>
                  {/* Says what the order does, because the order decides the
                    outcome and nothing else on screen reveals that. The former
                    explanation of what a signal IS is gone: the page is called
                    Signals and the sample below shows them rendered. */}
                  <p className="rsig-hint">
                    Within a group only the first matching definition is
                    displayed, so position decides the outcome.
                  </p>
                  {loadingSignals || !signals ? (
                    <Spinner />
                  ) : (
                    <SignalsGroups
                      saveSignals={this.saveSignals}
                      focus={this.state.focus}
                      compact
                      perDefinitionSave
                      onEditingChange={(editing) =>
                        this.setState({ editing })
                      }
                    />
                  )}
                  {/* Held back rather than applied, because a reload replaces
                      the definition being edited. Offered, not forced. */}
                  {this.state.agentChanged && (
                    <Alert variant="info" className="mt-3 mb-0 rsig-agent">
                      <span>
                        The assistant changed the signals while you were
                        editing.
                      </span>
                      <Button size="sm" onClick={this.reloadSignals}>
                        Load its version
                      </Button>
                    </Alert>
                  )}
                  {signalsSaved && (
                    <Alert variant="success" className="mt-3 mb-0">
                      Signals saved
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              <Card className="rsig-card rsig-sample">
                <Card.Body>
                  <h5 className="rsig-title">
                    <i className="fas fa-users rsig-icon" aria-hidden="true" />
                    Sample
                  </h5>
                  {/* Says which of the two things is happening, because they
                      look identical and mean opposite things: re-rendering the
                      SAME leads after an edit, or drawing a new hundred. A
                      refresh that arrives unannounced reads as the page losing
                      your place. */}
                  <p className="rsig-hint">
                    {spotCheckPage.loadingSpotCheck
                      ? 'Re-scoring these same leads with the signals you just changed...'
                      : 'A hundred leads from the validation set, scored by this model, with the signals each one would carry into the CRM. They stay put while you edit; Refresh draws a new hundred.'}
                  </p>
                  {loadSpotCheckError ? (
                    <Error message={loadSpotCheckError} />
                  ) : (
                    <LeadSampleTable
                      columnVisibility={this.state.columnVisibility}
                      falseNegativesOnly={this.state.falseNegativesOnly}
                      onToggleFalseNegatives={this.toggleFalseNegatives}
                      // Wrapped, so a click event cannot arrive where the
                      // options object goes. Refresh means a NEW hundred leads,
                      // which is the one control that still resamples.
                      onRefresh={() => this.loadSample()}
                      onJumpToSignal={this.jumpToSignal}
                      compact
                    />
                  )}
                </Card.Body>
              </Card>
            </div>

            <ScrollButton />
          </div>
        );
      }
    }
  )
);

export default ReviewSignalsPage;
